/* eslint semi: 1 */

import _ from 'lodash';
import { WebCrypto } from './WebCrypto.js';
import Utils from '../globals/utils.js';

export class DatatransLightbox {
  #datatrans = null;
  #datatransKey = '';
  #merchantId = '';
  #apiBaseUrl = '';
  #datatransSign = '';
  #datatransPayAPIRootURL = '';
  #messageEventListener = null;
  #onClose = () => {};
  #onReady = () => {};
  #onSubmit = () => {};

  constructor (datatrans, config, stage) {
    this.#datatrans = datatrans;
    this.#datatransKey = config?.DATATRANS_KEY ?? '';
    this.#merchantId = config?.DATATRANS_MERCHANT_ID ?? '';
    this.#apiBaseUrl = config?.API_BASE_URLS?.[stage] ?? '';
    this.#datatransSign = config?.DATATRANS_SIGN ?? '';
    this.#datatransPayAPIRootURL = config?.DATATRANS_PAY_API_ROOT_URL ?? '';
    this.addMessageListener();
  }

  open = (userToken, userInfo, redirectUrl) => {
    if (!this.#merchantId) return;

    if (_.isEmpty(userToken) || _.isEmpty(userInfo) || _.isEmpty(redirectUrl)) {
      console.error('Please provide user token, user information and frontend redirect url');
      return;
    }

    const merchantId = this.#merchantId;
    const amount = 0;
    const currency = userInfo?.currency ?? 'CHF';
    const refno = Math.floor(Math.random() * new Date().getTime());
    let sign = null;
    const datatransStartPayment = () => {
      this.#datatrans.startPayment({
        form: '#datatransLightboxForm',
        params: {
          merchantId,
          amount,
          currency,
          refno,
          paymentmethod: 'VIS,ECA,PAP',
          sign,
          token: userToken ?? '',
          frontendSuccessUrl: redirectUrl,
          frontendErrorUrl: redirectUrl,
          frontendCancelUrl: redirectUrl,
          successUrl: `${this.#apiBaseUrl}/payment/redirect`,
          cancelUrl: `${this.#apiBaseUrl}/payment/redirect`,
          errorUrl: `${this.#apiBaseUrl}/payment/redirect`
        }
      });
    };
    const createSignature = (key) => {
      const message = `${merchantId}${amount}${currency}${refno}`;
      const enc = new TextEncoder();
      const encoded = enc.encode(message);

      WebCrypto.sign(
        'HMAC',
        key,
        encoded
      )
        .then((signature) => {
          Utils.mainLoader(true);
          // Convert signature buffer to Hex
          sign = [...new Uint8Array(signature)].map(x => x.toString(16).padStart(2, '0')).join('');
          datatransStartPayment();
        })
        .catch((err) => {
          Utils.mainLoader(false);
          console.error('Failed to create signature', err);
        });
    };

    if (this.#datatransKey) {
      // importKey for Dynamic sign
      const keyData = new Uint8Array(this.#datatransKey.match(/../g).map(h => parseInt(h, 16))).buffer;
      WebCrypto.importKey(
        'raw',
        keyData,
        {
          name: 'HMAC',
          hash: { name: 'SHA-256' }
        },
        true,
        ['sign', 'verify']
      )
        .then((key) => {
          Utils.mainLoader(true);
          createSignature(key);
        })
        .catch((err) => {
          Utils.mainLoader(false);
          console.error('Failed to import key', err);
        });
    } else {
      sign = this.#datatransSign;
      datatransStartPayment();
    }
  };

  setOnClose = (callback) => {
    this.#onClose = _.isFunction(callback) ? callback : () => {};
  };

  setOnReady = (callback) => {
    this.#onReady = _.isFunction(callback) ? callback : () => {};
  };

  setOnSubmit = (callback) => {
    this.#onSubmit = _.isFunction(callback) ? callback : () => {};
  };

  onReady = () => {
    if (_.isFunction(this.#onReady)) {
      this.#onReady();
    }
  };

  onClose = () => {
    if (_.isFunction(this.#onClose)) {
      this.#onClose();
    }
  };

  onSubmit = () => {
    if (_.isFunction(this.#onSubmit)) {
      this.#onSubmit();
    }
  };

  addMessageListener = () => {
    if (!this.#messageEventListener) {
      this.#messageEventListener = this.handleMessage.bind(this);
      window.addEventListener('message', this.#messageEventListener);
    }
  };

  removeMessageListener = () => {
    if (this.#messageEventListener) {
      window.removeEventListener('message', this.#messageEventListener);
      this.#messageEventListener = null;
    }
  };

  handleMessage = ($event) => {
    // Make sure origin is from datatrans
    const datatransOrigin = (new URL(this.#datatransPayAPIRootURL)).origin;
    if ($event.origin !== datatransOrigin) return;

    if (['frameReady'].includes($event.data)) {
      this.onReady();
    }

    if (['cancel'].includes($event.data)) {
      this.onClose();
    }

    // Show loader when submitting payment to inform user that payment is being process
    if ($event.data.method && $event.data.method === 'post') {
      this.onSubmit();
    }
  };
}

export class DatatransRedirect {
  #responseData = {};

  constructor (data) {
    this.#responseData = data ?? {};
  }

  setResponseData = (responseData) => {
    this.#responseData = responseData;
  };

  getResponseData = () => {
    return this.#responseData;
  };

  getResponseDataFromUrl = () => {
    return this.extractResponseDataFromUrl(window.location.href);
  };

  extractResponseDataFromUrl = (url) => {
    let targetUrl = null;

    try {
      targetUrl = new URL(url);
    } catch (error) {
      return {};
    }

    const datatransParamsToCheck = ['datatransRedirect', 'status', 'refno'];
    const queryParams = targetUrl.searchParams;
    const queryParamsObject = _.reduce([...queryParams.entries()], (result, [key, value]) => {
      result[key] = value;
      return result;
    }, {});
    const hasAllParams = _.every(datatransParamsToCheck, (key) => _.has(queryParamsObject, key));

    if (hasAllParams && queryParamsObject.status === 'error') {
      return {
        failed: true,
        data: queryParamsObject
      };
    }

    if (hasAllParams && queryParamsObject.status === 'success') {
      return {
        success: true,
        data: queryParamsObject
      };
    }

    return {};
  };

  watchResponseData = (callback) => {
    const getData = this.getResponseDataFromUrl();

    if (_.isFunction(callback)) {
      callback(getData);
    }
  };
}
